import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../api";
import type { operations } from "../../api/schema";
import type { CursorPage, Datified, Schemas } from "../../api/types";
import { POLL_BASE_MS, POLL_LIVE_MS } from "./intervals";

export type MessageState = Schemas["Message"]["state"];
export type Message = Datified<
  Schemas["Message"],
  "created_at" | "sent_at" | "next_attempt_at" | "expires_at"
>;
export type MessageDetail = Message &
  Omit<Schemas["MessageDetail"], keyof Schemas["Message"]>;

export interface MessageListFilters {
  state?: string;
  category?: string;
  sender?: string;
  batch_id?: string;
  q?: string;
  /** Exact client reference. */
  reference?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

function qs(f: MessageListFilters): string {
  const u = new URLSearchParams();
  if (f.state) u.set("state", f.state);
  if (f.category) u.set("category", f.category);
  if (f.sender) u.set("sender", f.sender);
  if (f.batch_id) u.set("batch_id", f.batch_id);
  if (f.q) u.set("q", f.q);
  if (f.reference) u.set("reference", f.reference);
  if (f.from) u.set("from", f.from);
  if (f.to) u.set("to", f.to);
  if (f.cursor) u.set("cursor", f.cursor);
  if (f.limit) u.set("limit", String(f.limit));
  const s = u.toString();
  return s ? "?" + s : "";
}

export function useMessageList(filters: MessageListFilters = {}) {
  return useQuery<CursorPage<Message>>({
    queryKey: ["messages", filters],
    queryFn: () => apiGet<CursorPage<Message>>("/messages" + qs(filters)),
    refetchInterval: (query) =>
      isMessageListLive(filters, query.state.data?.items)
        ? POLL_LIVE_MS
        : POLL_BASE_MS,
  });
}

/**
 * Distinct recipient addresses matching `term` — the suggestion source for
 * the Messages search autocomplete. A failed fetch just yields no
 * suggestions (the field keeps working as a plain text filter), so errors
 * are swallowed here.
 */
export async function fetchRecipientSuggestions(term: string): Promise<string[]> {
  const q = term.trim();
  if (q.length < 2) return [];
  try {
    const page = await apiGet<CursorPage<Message>>("/messages" + qs({ q, limit: 10 }));
    return [...new Set(page.items.map((m) => m.to_address))];
  } catch {
    return [];
  }
}

/** Delivery can still change these states, so their views poll. */
export function isLiveState(state: MessageState): boolean {
  return state === "pending" || state === "leased";
}

/**
 * Whether the messages list is worth polling.
 *
 * Only on the first page: a set cursor means the reader paged here
 * deliberately, and a keyset page is read, not watched. And only when
 * something on screen can still change — either a row in a live state, or a
 * filter pinned to one, because an empty pending queue is exactly the view
 * you leave open waiting for arrivals.
 */
export function isMessageListLive(
  filters: Pick<MessageListFilters, "state" | "cursor">,
  items: Message[] | undefined,
): boolean {
  if (filters.cursor) return false;
  // The filter is free text off the URL; anything that isn't a state simply
  // isn't a live one.
  if (filters.state) return isLiveState(filters.state as MessageState);
  return (items ?? []).some((m) => isLiveState(m.state));
}

export function useMessageDetail(id: string | null) {
  return useQuery<MessageDetail>({
    queryKey: ["messages", "detail", id],
    queryFn: () => apiGet<MessageDetail>(`/messages/${id}`),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data && isLiveState(query.state.data.state)
        ? POLL_LIVE_MS
        : POLL_BASE_MS,
  });
}

export type MessageEventType = Schemas["MessageEventType"];
export type MessageEvent = Datified<
  Schemas["MessageEvent"],
  "created_at" | "next_attempt_at"
>;

/**
 * GET /api/messages/{id}/events — the delivery timeline, oldest first. Keep
 * the key under the ["messages"] prefix: message actions invalidate that
 * prefix, which refreshes the timeline too.
 */
export function useMessageEvents(
  id: string | null,
  { live = false }: { live?: boolean } = {},
) {
  return useQuery<MessageEvent[]>({
    queryKey: ["messages", "events", id],
    queryFn: async () => {
      const res = await apiGet<{ items: MessageEvent[] }>(`/messages/${id}/events`);
      return res.items;
    },
    enabled: !!id,
    refetchInterval: live ? POLL_LIVE_MS : POLL_BASE_MS,
  });
}

export type MessageAction = "retry" | "cancel" | "redeliver" | "rerender";

/** The redeliver body: `to` sends the copy to another recipient. */
export type RedeliverRequest = Schemas["RedeliverRequest"];

/** POST /api/messages/{id}/(retry|cancel|redeliver|rerender). */
export function useMessageAction() {
  const qc = useQueryClient();
  return useMutation<
    Message,
    unknown,
    { id: string; action: MessageAction; body?: RedeliverRequest }
  >({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id, action, body }) => apiPost<Message>(`/messages/${id}/${action}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messages"] });
      void qc.invalidateQueries({ queryKey: ["batches"] });
      void qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export type BulkRetryResponse =
  operations["adminBulkRetryMessages"]["responses"][200]["content"]["application/json"];

/**
 * POST /api/messages/bulk-retry — retries many messages in one call (1–500
 * ids). Non-retryable messages are not an error: the server reports them per
 * id in `skipped` and the page surfaces that as a warning toast.
 */
export function useBulkRetryMessages() {
  const qc = useQueryClient();
  return useMutation<BulkRetryResponse, unknown, { ids: string[] }>({
    mutationFn: (input) => apiPost<BulkRetryResponse>("/messages/bulk-retry", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messages"] });
      void qc.invalidateQueries({ queryKey: ["batches"] });
      void qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export type AdminSendMessageInput = Schemas["AdminSendMessageRequest"];

/**
 * POST /api/messages — admin compose: one message, raw content XOR a
 * published template. Errors render in the compose modal, not as a toast.
 */
export function useAdminSendMessage() {
  const qc = useQueryClient();
  return useMutation<Schemas["EnqueueResponse"], unknown, AdminSendMessageInput>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPost<Schemas["EnqueueResponse"]>("/messages", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messages"] });
      void qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}
